import React, { useState, useEffect, useRef } from "react";
import { Camera, Upload, X, Image as ImageIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth";

/**
 * PhotoUploader — a reusable evidence-photo widget.
 *
 * Props:
 *  - scope: "incident" | "albaran" | "shipment_evidence" (required, controls storage path)
 *  - scopeId: optional entity id to filter files
 *  - value: array of FileRecord ids attached to the parent entity
 *  - onChange: (newIds) => void
 *  - max: cap for uploads (default 6)
 *  - label: header label
 *  - testid: base testid
 *
 * Uses backend `POST /api/files/upload?scope=...` and renders thumbnails via
 * `GET /api/files/{path}` fetched as blob (auth-header safe for <img>).
 */
export default function PhotoUploader({ scope, scopeId, value = [], onChange, max = 6, label = "Fotos", testid = "photo-uploader", accept = "image/*,application/pdf", maxMB }) {
  const { api, token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [records, setRecords] = useState([]);   // FileRecords for value ids
  const [blobs, setBlobs] = useState({});       // { fileId: blobUrl }
  const fileInput = useRef(null);
  const cameraInput = useRef(null);
  // Master attachments can be large (manifests, scans, bills of lading).
  const sizeCapMB = maxMB ?? (scope === "master" ? 100 : 20);

  // Hydrate records + blobs when value ids change
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!value || value.length === 0) { setRecords([]); return; }
      try {
        const { data } = await api.get("/files", { params: scopeId ? { scope, scope_id: scopeId } : { scope } });
        if (cancelled) return;
        const recs = (data || []).filter(r => value.includes(r.id));
        setRecords(recs);
        // Fetch each blob for display
        const map = {};
        for (const r of recs) {
          try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.storage_path}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (resp.ok) {
              const b = await resp.blob();
              map[r.id] = URL.createObjectURL(b);
            }
          } catch (e) { console.warn("thumb fetch failed", e); }
        }
        if (!cancelled) setBlobs(map);
      } catch (e) { console.warn("PhotoUploader load failed", e); }
    };
    load();
    return () => {
      cancelled = true;
      Object.values(blobs).forEach(u => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.join("|"), scope, scopeId]);

  const uploadFile = async (file) => {
    if (!file) return;
    if (value.length >= max) return toast.error(`Máximo ${max} archivos`);
    if (file.size > sizeCapMB * 1024 * 1024) return toast.error(`Máx ${sizeCapMB}MB`);
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/files/upload?scope=${scope}${scopeId ? `&scope_id=${scopeId}` : ""}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange([...value, data.id]);
      toast.success("Archivo subido");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error subiendo archivo");
    } finally { setBusy(false); }
  };

  const removePhoto = (id) => {
    onChange(value.filter(x => x !== id));
  };

  return (
    <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 space-y-2 bg-slate-50/60" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-semibold tabular-nums">{value.length}/{max}</span>
        </div>
        <div className="flex gap-2">
          <input ref={cameraInput} type="file" accept="image/*" capture="environment" onChange={e => uploadFile(e.target.files?.[0])} className="hidden" />
          <input ref={fileInput} type="file" accept={accept} onChange={e => uploadFile(e.target.files?.[0])} className="hidden" />
          <button type="button" disabled={busy || value.length >= max} onClick={() => cameraInput.current?.click()} data-testid={`${testid}-camera`} className="text-xs font-semibold px-2.5 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1 disabled:opacity-50">
            <Camera className="w-3 h-3" /> Cámara
          </button>
          <button type="button" disabled={busy || value.length >= max} onClick={() => fileInput.current?.click()} data-testid={`${testid}-file`} className="text-xs font-semibold px-2.5 py-1 rounded border border-slate-300 hover:border-slate-400 flex items-center gap-1 disabled:opacity-50">
            <Upload className="w-3 h-3" /> Subir
          </button>
        </div>
      </div>
      {records.length === 0 ? (
        <div className="text-center py-4 text-slate-400 text-xs">
          <ImageIcon className="w-8 h-8 mx-auto opacity-40 mb-1" />
          Sin fotos aún
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {records.map(r => (
            <div key={r.id} className="relative group border border-slate-200 rounded-lg overflow-hidden bg-white aspect-square" data-testid={`${testid}-item-${r.id}`}>
              {blobs[r.id] ? (
                <img src={blobs[r.id]} alt={r.original_filename} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-300"><ImageIcon className="w-6 h-6" /></div>
              )}
              <button type="button" onClick={() => removePhoto(r.id)} className="absolute top-1 end-1 p-1 bg-red-600/90 text-white rounded hover:bg-red-700 opacity-80 group-hover:opacity-100" title="Quitar">
                <Trash2 className="w-3 h-3" />
              </button>
              <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate">
                {r.created_by_name || "—"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
